import { useCallback } from 'react';

import { useLazyGetLocationShipmentsQuery } from '../api/receiveApi';
import { normalizeShipmentChildren } from '../lib/normalize';
import type { ReceiveChildrenById } from './types';

export interface UseLocationShipmentsOptions {
  projectId?: number;
  siteId?: number;
  /** Sole error channel — the returned promise always resolves, callers don't need try/catch */
  onError?: (error: unknown) => void;
}

export interface FetchLocationShipmentsArgs {
  /** Composite `siteId~id` from the `/locations` response. */
  locationId: string;
  /** Row the children belong to (their `parentId`). */
  rowId: string;
  /** Level of that row; children are nested below it. */
  level: number;
  /** `true` keeps the backend's date level (Customer Deliveries). */
  groupByDate: boolean;
}

/**
 * Fetches one location's shipments when its row is expanded, and returns them
 * already shaped as table rows. Wraps the raw lazy query with unwrap + error
 * handling so callers never touch RTK Query internals — mirrors
 * `useExportServiceRequests` / `useImportSrf`.
 *
 * In grouped mode a single response fills BOTH the date rows and their
 * consignments, so only the deeper balances level costs another request.
 *
 * Resolves to `null` when the request failed (the error went to `onError`),
 * which callers distinguish from an empty `{}`.
 */
export const useLocationShipments = ({ projectId, siteId, onError }: UseLocationShipmentsOptions = {}) => {
  const [fetchShipments, { isFetching, isError, error }] = useLazyGetLocationShipmentsQuery();

  const fetchLocationShipments = useCallback(
    async ({
      locationId,
      rowId,
      level,
      groupByDate
    }: FetchLocationShipmentsArgs): Promise<ReceiveChildrenById | null> => {
      try {
        const response = await fetchShipments({ locationId, projectId, siteId, groupByDate }).unwrap();

        return normalizeShipmentChildren(rowId, response, level);
      } catch (error) {
        onError?.(error);
        return null;
      }
    },
    [fetchShipments, projectId, siteId, onError]
  );

  return {
    fetchLocationShipments,
    // Scoped to the MOST RECENT request, not to any particular row: several rows
    // can be expanded at once and they share one trigger. Each call still
    // resolves with its own data — per-row pending/error state must come from
    // the caller's own bookkeeping, not from these flags.
    isFetching,
    isError,
    error
  };
};
